import { ref, watch, onBeforeUnmount } from '@vue/composition-api'

/**
 * Composable for autosave functionality
 * @param {Function} saveFunction - Function to call for saving (receives data as parameter)
 * @param {Object|Function} dataSource - Reactive data object or getter function to watch for changes
 * @param {Number} interval - Autosave interval in milliseconds (default: 30000 = 30 seconds)
 * @returns {Object} - { isAutosaving, lastAutosaveTime, triggerManualSave }
 */
export default function useAutosave(saveFunction, dataSource, interval = 30000) {
  const isAutosaving = ref(false)
  const lastAutosaveTime = ref(null)
  const autosaveTimer = ref(null)
  const isDirty = ref(false)

  // Determine if dataSource is a getter function or a reactive object
  const dataGetter = typeof dataSource === 'function' ? dataSource : () => dataSource

  // Function to perform autosave
  const performAutosave = async () => {
    if (!isDirty.value || isAutosaving.value) return

    try {
      isAutosaving.value = true
      const data = dataGetter()
      console.log('Saving data:', data)
      await saveFunction(data)
      lastAutosaveTime.value = new Date()
      isDirty.value = false
    } catch (error) {
      console.error('Autosave error:', error)
    } finally {
      isAutosaving.value = false
    }
  }

  // Watch for data changes
  // Use a more reliable approach: serialize the data to detect changes
  watch(
    () => {
      const data = dataGetter()
      // Create a deep copy for comparison
      return JSON.stringify(data)
    },
    (_newVal, oldVal) => {
      // Skip first run when oldVal is undefined (component just mounted)
      if (oldVal === undefined) return

      isDirty.value = true
      // Reset timer on every change
      if (autosaveTimer.value) {
        clearTimeout(autosaveTimer.value)
      }
      autosaveTimer.value = setTimeout(performAutosave, interval)
    }
  )

  // Manual save trigger (useful for step navigation)
  // Forces save even if not dirty, and clears pending timer
  const triggerManualSave = async (force = false) => {
    // Clear any pending autosave timer
    if (autosaveTimer.value) {
      clearTimeout(autosaveTimer.value)
      autosaveTimer.value = null
    }

    // If force=true, bypass isDirty check
    if (force || isDirty.value) {
      if (isAutosaving.value) return // Don't interrupt ongoing save

      try {
        isAutosaving.value = true
        const data = dataGetter()
        await saveFunction(data)
        lastAutosaveTime.value = new Date()
        isDirty.value = false
      } catch (error) {
        console.error('Manual save error:', error)
      } finally {
        isAutosaving.value = false
      }
    }
  }

  // Cleanup on component unmount
  onBeforeUnmount(() => {
    if (autosaveTimer.value) {
      clearTimeout(autosaveTimer.value)
    }
    // Optionally perform final save before unmount
    // if (isDirty.value) {
    //   performAutosave()
    // }
  })

  return {
    isAutosaving,
    lastAutosaveTime,
    triggerManualSave,
    isDirty,
  }
}
